<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Search Input -->
              <base-input
                col="12"
                type="text"
                :placeholder="$t('chatbotFaqs.searchPlaceholder')"
                v-model.trim="filterOptions.q"
              />
              <!-- End:: Search Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("chatbotFaqs.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div
          class="title_route_wrapper"
          v-if="$can('chatbotfaqs create', 'chatbotfaqs')"
        >
          <button @click="openCreateModal" class="btn">
            {{ $t("chatbotFaqs.create") }}
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('chatbotFaqs.loadingFaqs')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("chatbotFaqs.noFaqs") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serialNumber`]="{ item }">
          <p>{{ item.serialNumber }}</p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Question -->
        <template v-slot:[`item.question`]="{ item }">
          <p>{{ item.question  }}</p>
        </template>
        <!-- End:: Question -->

        <!-- Start:: Answer -->
        <template v-slot:[`item.answer`]="{ item }">
          <p class="text-truncate" style="max-width: 300px">
            {{ item.answer}}
          </p>
        </template>
        <!-- End:: Answer -->

        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('chatbotfaqs activate', 'chatbotfaqs')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="toggleActivation(item)"
            ></v-switch>
          </div>
        </template>
        <!-- End:: Activation -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('chatbotfaqs delete', 'chatbotfaqs')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="openDeleteModal(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('chatbotfaqs edit', 'chatbotfaqs')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editFaq(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('chatbotfaqs show', 'chatbotfaqs')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="viewFaqDetails(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <template v-else>
              <i
                class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"
              ></i>
            </template>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <template>
        <div class="pagination_container text-center mt-3 mb-0">
          <v-pagination
            v-model="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="6"
            @input="updateRouterQueryParam($event)"
            :prev-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
            "
            :next-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
            "
          ></v-pagination>
        </div>
      </template>
      <!--  =========== End:: Pagination =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: FAQ Form Modal -->
    <ChatbotFaqForm
      :faqData="selectedFaq"
      :showModal="showFormModal"
      :isEdit="isEditMode"
      @closeModal="closeFormModal"
      @refreshData="getAllFaqs"
    />
    <!-- End:: FAQ Form Modal -->

    <!-- Start:: FAQ Detail Modal -->
    <ChatbotFaqDetail
      :faqData="selectedFaq"
      :showModal="showDetailModal"
      @closeModal="showDetailModal = false"
    />
    <!-- End:: FAQ Detail Modal -->

    <!-- Start:: Delete Modal -->
    <v-dialog v-model="showDeleteModal">
      <v-card>
        <v-card-title class="text-h5 justify-center" v-if="selectedFaq">
          {{ $t("chatbotFaqs.confirmDelete") }}
        </v-card-title>
        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            :disabled="isDeleting"
            :loading="isDeleting"
            @click="deleteFaq"
          >
            {{ $t("BUTTONS.ok") }}
          </v-btn>
          <v-btn
            class="modal_cancel_btn"
            :disabled="isDeleting"
            @click="showDeleteModal = false"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Delete Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ChatbotFaqForm from "./ChatbotFaqForm.vue";
import ChatbotFaqDetail from "./ChatbotFaqDetail.vue";

export default {
  name: "ChatbotFaqsList",

  components: {
    ChatbotFaqForm,
    ChatbotFaqDetail,
  },

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      isWaitingRequest: false,
      isDeleting: false,
      // End:: Loader Control Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        q: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("chatbotFaqs.serialNumber"),
          value: "serialNumber",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("chatbotFaqs.question"),
          value: "question",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("chatbotFaqs.answer"),
          value: "answer",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("chatbotFaqs.status"),
          value: "is_active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("chatbotFaqs.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Modal Data
      showFormModal: false,
      showDetailModal: false,
      showDeleteModal: false,
      selectedFaq: null,
      isEditMode: false,
      // End:: Modal Data
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.getAllFaqs();
    },
  },

  methods: {
    // Start:: Get All FAQs
    async getAllFaqs() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "chatbot-faqs",
          params: {
            page: this.paginations.current_page,
            question: this.filterOptions.q,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
        this.tableRows.forEach((item, index) => {
          item.serialNumber =
            (this.paginations.current_page - 1) *
              this.paginations.items_per_page +
            index +
            1;
        });
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get All FAQs

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllFaqs();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.q = null;
      this.paginations.current_page = 1;
      this.getAllFaqs();
    },
    // End:: Reset Filter

    // Start:: Open Create Modal
    openCreateModal() {
      this.selectedFaq = null;
      this.isEditMode = false;
      this.showFormModal = true;
    },
    // End:: Open Create Modal

    // Start:: Edit FAQ
    editFaq(faq) {
      this.selectedFaq = { ...faq };
      this.isEditMode = true;
      this.showFormModal = true;
    },
    // End:: Edit FAQ

    // Start:: View FAQ Details
    viewFaqDetails(faq) {
      this.selectedFaq = faq;
      this.showDetailModal = true;
    },
    // End:: View FAQ Details

    // Start:: Close Form Modal
    closeFormModal() {
      this.showFormModal = false;
      this.selectedFaq = null;
      this.isEditMode = false;
    },
    // End:: Close Form Modal

    // Start:: Toggle Activation
    async toggleActivation(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `chatbot-faqs/${item.id}/activate`,
        });
        this.getAllFaqs();
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.getAllFaqs();
        this.$message.error(error.response?.data?.message);
      }
    },
    // End:: Toggle Activation

    // Start:: Open Delete Modal
    openDeleteModal(faq) {
      this.selectedFaq = faq;
      this.isDeleting = false;
      this.showDeleteModal = true;
    },
    // End:: Open Delete Modal

    // Start:: Delete FAQ
    async deleteFaq() {
      if (!this.selectedFaq?.id || this.isDeleting) {
        return;
      }

      this.isDeleting = true;

      try {
        await this.$axios({
          method: "DELETE",
          url: `chatbot-faqs/${this.selectedFaq.id}`,
        });

        this.$message.success(this.$t("chatbotFaqs.deleteSuccess"));
        this.showDeleteModal = false;
        this.selectedFaq = null;
        this.getAllFaqs();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("errorOccurred")
        );
      } finally {
        this.isDeleting = false;
      }
    },
    // End:: Delete FAQ

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    // End:: Update Router Query Param
  },

  created() {
    this.paginations.current_page = Number(this.$route.query.page) || 1;
    this.getAllFaqs();
  },
};
</script>

<style scoped lang="scss">
.status_badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
}
</style>
